import type { Client } from "@/hooks/useClients";
import type { DropboxFolder } from "@/hooks/useDropboxFolders";

const DASH_CLASS = "\\-\\u2013\\u2014"; // hyphen, en dash, em dash

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * A facility's `dropbox_folder_prefix` (e.g. "GWH -") as a dash-tolerant
 * regex, so "GWH - Pisoni", "GWH – Pisoni" (en dash), and "GWH-Pisoni" all
 * match. Single source of truth for "does this folder belong to this
 * facility" -- used by both the raw Dropbox listing filter
 * (useDropboxClientFolders) and the fuzzy client matcher below.
 */
function facilityPrefixPattern(dropboxFolderPrefix: string): RegExp {
  const code = dropboxFolderPrefix.trim().replace(new RegExp(`[${DASH_CLASS}\\s]+$`), "");
  return new RegExp(`^${escapeRegExp(code)}\\s*[${DASH_CLASS}]\\s*`, "i");
}

export function matchesFacilityPrefix(folderName: string, dropboxFolderPrefix: string): boolean {
  return facilityPrefixPattern(dropboxFolderPrefix).test(folderName);
}

function stripFacilityPrefix(folderName: string, dropboxFolderPrefix: string): string {
  return folderName.replace(facilityPrefixPattern(dropboxFolderPrefix), "").trim();
}

/**
 * Matches a client to a Dropbox client folder for a given facility. Priority
 * 1 is the client's already-saved folder for this facility (from
 * client_facility_folder), if any; priority 2 falls back to name/code
 * pattern matching against that facility's folder-naming convention (e.g.
 * "GWH - ClientName", "RP - ClientName").
 */
export function matchClientDropboxFolder(
  client: Pick<Client, "name" | "code">,
  dropboxFolders: DropboxFolder[],
  facilityPrefix: string,
  savedFolder?: string | null
): string | null {
  if (savedFolder) {
    const directMatch = dropboxFolders.find(
      (folder) => folder.name.toLowerCase() === savedFolder.toLowerCase()
    );
    if (directMatch) return directMatch.name;
  }

  const clientName = client.name.toLowerCase();
  const clientCode = client.code?.toLowerCase();

  // Extract first significant word from client name (e.g., "Alma" from "Alma de Cattleya")
  const clientFirstWord = clientName.split(/[\s&,]+/)[0];

  const matchingFolder = dropboxFolders.find((folder) => {
    const folderNameLower = folder.name.toLowerCase();

    // Extract client-specific part from folder (e.g., "Pisoni" from "RP - Pisoni")
    const folderClientPart = stripFacilityPrefix(folderNameLower, facilityPrefix);

    if (folderNameLower === clientName) return true;
    if (folderNameLower.includes(clientName)) return true;
    if (clientName.includes(folderClientPart) && folderClientPart.length >= 3) return true;
    if (clientFirstWord && folderClientPart.includes(clientFirstWord) && clientFirstWord.length >= 3) return true;
    if (clientCode && folderNameLower.includes(clientCode)) return true;

    return false;
  });

  return matchingFolder?.name ?? null;
}
